import React from 'react';
import propTypes from 'prop-types';
;
import { Button, Card, CardContent, Columns, Column } from 'src/ui/mui-ui-kit';;
import { Field, Form } from 'react-final-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  UiInputAdapter,
  UiToggleAdapter,
  UiReadOnlyQuill,
  InnerLoader,
} from 'src/ui';
import classNames from 'classnames';
import { hasAcknowledgePermissions, hasResolvePermissions } from '../utils';
import { ComplianceActions } from './ComplianceActions';
import { PASSED_BACKGROUND_VALIDATION_STATUSES } from '../../ui/const';

export const ComplianceDetailEditForm = ({
  id,
  user,
  className,
  backgroundAssessment,
  backgroundAssessmentLog,
  unsavedLogs,
  setUnsavedLogs,
}) => {
  const initialValues = { backgroundAssessment };
  const [acknowledged, setAcknowledged] = React.useState(false);
  const [resolved, setResolved] = React.useState(false);
  const [acknowledgingId, setAcknowledgingId] = React.useState(null);
  const [resolvingId, setResolvingId] = React.useState(null);
  const [addingUserComment, setAddingUserComment] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setAcknowledged(backgroundAssessment.acknowledged);
    setResolved(backgroundAssessment.resolved);
  }, [backgroundAssessment]);

  const passed = React.useMemo(() => {
    return PASSED_BACKGROUND_VALIDATION_STATUSES.includes(
      backgroundAssessment.backgroundAssessmentTypeResult.result
    );
  }, [backgroundAssessment]);

  const unsavedAcknowledge = React.useMemo(() => {
    return unsavedLogs.some(
      log =>
        log.backgroundAssessmentLogType.id ===
        backgroundAssessmentLog.acknowledgedBackgroundAssessmentLogType
    );
  }, [unsavedLogs, backgroundAssessmentLog]);

  const unsavedResolve = React.useMemo(() => {
    return unsavedLogs.some(
      log =>
        log.backgroundAssessmentLogType.id ===
        backgroundAssessmentLog.resolvedBackgroundAssessmentLogType
    );
  }, [unsavedLogs, backgroundAssessmentLog]);

  const handleAcknowledge = React.useCallback(() => {
    setAcknowledgingId(null);
  }, []);

  const handleResolve = React.useCallback(() => {
    setResolvingId(null);
  }, []);

  const handleAddUserComment = React.useCallback(() => {
    setAddingUserComment(null);
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    if (unsavedAcknowledge) {
      setAcknowledged(true);
    }
    if (unsavedResolve) {
      setAcknowledged(true);
      setResolved(true);
    }
    setLoading(false);
  };

  const canResolve = hasResolvePermissions(user);
  const canAcknowledge = hasAcknowledgePermissions(user);
  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={initialValues}
      subscription={{
        submitting: true,
      }}
      render={({ submitting, handleSubmit }) => (
        <>
          <Card>
            <CardContent>
              <form onSubmit={handleSubmit} noValidate>
                <Columns isMultiline className={className}>
                  <Column isSize="1/3" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      id="accountBusinessName"
                      name="backgroundAssessment.account.businessName"
                      label="Account"
                      disabled
                    />
                  </Column>
                  <Column isSize="1/3" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      id="type"
                      name="backgroundAssessment.backgroundAssessmentType.type"
                      label="Type"
                      disabled
                    />
                  </Column>
                  <Column isSize="1/3" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      id="result"
                      name="backgroundAssessment.backgroundAssessmentTypeResult.result"
                      label="Result"
                      disabled
                    />
                  </Column>
                  <Column isSize="1/3" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      id="userFirstName"
                      name="backgroundAssessment.user.firstName"
                      label="Person First Name"
                      disabled
                    />
                  </Column>
                  <Column isSize="1/3" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      id="userLastName"
                      name="backgroundAssessment.user.lastName"
                      label="Person Last Name"
                      disabled
                    />
                  </Column>
                  <Column isSize="1/3" className="is-one-third-fullhd" />
                  <Column isSize="1/3" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      id="locationAddress"
                      name="backgroundAssessment.location.address"
                      label="Location Address"
                      disabled
                    />
                  </Column>
                  <Column isSize="1/3" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      id="locationCity"
                      name="backgroundAssessment.location.city"
                      label="Location City"
                      disabled
                    />
                  </Column>
                  <Column isSize="1/3" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      id="locationState"
                      name="backgroundAssessment.location.state"
                      label="Location State"
                      disabled
                    />
                  </Column>
                  <Column isSize="1/3" className="is-one-third-fullhd">
                    {backgroundAssessment.document && (
                      <a
                        href={backgroundAssessment.document.documentFile}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="has-text-link"
                      >
                        <Field
                          component={UiInputAdapter}
                          id="document"
                          name="backgroundAssessment.document.documentName"
                          label="Document"
                          disabled
                        />
                      </a>
                    )}
                  </Column>
                  <Column isSize="1/3" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      id="documentType"
                      name="backgroundAssessment.document.documentType"
                      label="Document Type"
                      disabled
                    />
                  </Column>
                  <Column isSize="1/3" className="is-one-third-fullhd">
                    <Field
                      component={UiInputAdapter}
                      id="documentUploadedBy"
                      name="backgroundAssessment.document.uploadedBy"
                      label="Document Uploaded By"
                      disabled
                    />
                  </Column>
                  <Column
                    isSize="1/3"
                    className={classNames('is-one-half-fullhd', {
                      'is-hidden': passed,
                    })}
                  >
                    <Field
                      component={UiToggleAdapter}
                      name="backgroundAssessment.acknowledged"
                      id="acknowledged"
                      type="checkbox"
                      checked={
                        acknowledged || unsavedAcknowledge || unsavedResolve
                      }
                      disabled={
                        acknowledged ||
                        unsavedAcknowledge ||
                        unsavedResolve ||
                        !canAcknowledge
                      }
                      onClick={() => setAcknowledgingId(id)}
                    >
                      Acknowledged
                    </Field>
                  </Column>
                  <Column
                    isSize="1/3"
                    className={classNames('is-one-half-fullhd', {
                      'is-hidden': passed,
                    })}
                  >
                    <Field
                      component={UiToggleAdapter}
                      name="backgroundAssessment.resolved"
                      id="resolved"
                      type="checkbox"
                      checked={resolved || unsavedResolve}
                      disabled={resolved || unsavedResolve || !canResolve}
                      onClick={() => setResolvingId(id)}
                    >
                      Resolved
                    </Field>
                  </Column>
                  <Column isSize="1/3" className="is-one-third-fullhd">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                      <Button
                        isColor="gradient-bright"
                        onClick={() => setAddingUserComment(id)}
                      >
                        <span className="icon">
                          <FontAwesomeIcon icon={['fal', 'plus']} />
                        </span>
                        <span>Add Log</span>
                      </Button>
                    </div>
                  </Column>
                  <Column isSize="full">
                    <UiReadOnlyQuill
                      id="supplementalInformation"
                      label="Supplemental Information"
                      value={backgroundAssessment?.supplementalInformation}
                    />
                  </Column>
                </Columns>
              </form>
            </CardContent>
            <InnerLoader visible={loading || submitting} />
          </Card>
          <ComplianceActions
            confirmChanges
            unsavedLogs={unsavedLogs}
            setUnsavedLogs={setUnsavedLogs}
            submitting={submitting}
            acknowledgingId={acknowledgingId}
            resolvingId={resolvingId}
            addingUserComment={addingUserComment}
            onAcknowledge={handleAcknowledge}
            onResolve={handleResolve}
            onAddUserComment={handleAddUserComment}
          />
        </>
      )}
    />
  );
};

ComplianceDetailEditForm.propTypes = {
  id: propTypes.number.isRequired,
  user: propTypes.object.isRequired,
  className: propTypes.string,
  backgroundAssessment: propTypes.object.isRequired,
  backgroundAssessmentLog: propTypes.object.isRequired,
  unsavedLogs: propTypes.array.isRequired,
  setUnsavedLogs: propTypes.func.isRequired,
};

ComplianceDetailEditForm.defaultProps = {
  className: '',
};
